import { Box, Button, Icon, Typography, useTheme } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import React from "react";

import { AvailableTime } from "../../AvailableTimesInput/types";
import {
  TIMES_OF_DAY,
  TimeOfDay,
  checkIsSelected,
  makeAvailableTimes,
  useTimeOfDayString,
} from "../../AvailableTimesInput/utils";

export default function AvailableTimesQuickSelectButtons({
  availableTimes,
  onUpdateAll,
}: {
  availableTimes: AvailableTime[];
  onUpdateAll: (_ea: AvailableTime[]) => void;
}) {
  const [isSelectedCustom, setSelectedCustom] = React.useState<boolean>(false);

  const handleClick = async (from: number, to: number, days?: number[]) => {
    setSelectedCustom(false);
    await onUpdateAll(makeAvailableTimes(from, to, days));
  };

  const optionSelected: TimeOfDay = React.useMemo(() => {
    if (isSelectedCustom) {
      return TimeOfDay.Custom;
    }

    const matchedOption = TIMES_OF_DAY.filter(
      tod => tod.label !== TimeOfDay.Custom && tod.range.length == 2,
    ).find(tod =>
      checkIsSelected(availableTimes, tod.range[0]!, tod.range[1]!, tod.days),
    )?.label;

    if (matchedOption) {
      return matchedOption;
    }

    return TimeOfDay.Custom;
  }, [isSelectedCustom, availableTimes]);

  const timeOfDayString = useTimeOfDayString();
  const theme = useTheme();

  return (
    <Box
      sx={{
        maxWidth: "100%",
        display: "flex",
        gap: "12px",
        flexWrap: "nowrap",
        [theme.breakpoints.down("sm")]: {
          flexDirection: "column",
        },
      }}
    >
      {TIMES_OF_DAY.map(({ icon, label, range, days }) => {
        const isSelected = optionSelected === label;
        return (
          <Button
            key={label}
            size="small"
            onClick={(e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e?.currentTarget.blur();
              handleClick(range[0]!, range[1]!, days);
            }}
            variant={isSelected ? "contained" : "outlined"}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: "0.5em",
              minWidth: 0,
              flex: 1,
              color: isSelected ? "common.white" : "common.black",
              borderColor: isSelected ? "primary.700" : "neutral.light",
            }}
          >
            <Icon
              icon={icon}
              sx={{ fontSize: "1.5em", marginBottom: "0.25em" }}
            />
            <Typography variant="subtitle1">
              {timeOfDayString(label)}
            </Typography>
            <Typography variant="caption">
              {SearchFilters.timeRangeToString(range[0], range[1])}
            </Typography>
          </Button>
        );
      })}
    </Box>
  );
}
